import { ChevronDown16, ChevronRight16 } from '@carbon/icons-react';
// import { Divider as AntdDivider } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Space } from 'api/space';
import { ReactComponent as Folder16 } from 'assets/svgs/folder-filled.svg';
import { ALL_COLORS_WITHOUT_GRAY } from 'components/colors';
import { useIsResponsive, useIsTablet } from 'hooks/use-media-query';
import { getRandomColor } from 'utils/get-random-color';
import {
  AccountsSidebar,
  ProjectChevronWrapper,
  ProjectEmptyState,
  ProjectName,
  ProjectNameAndChevronWrapper,
  ProjectNameWrapper,
  ProjectWrapper,
  SideBarItem,
  SideBarItemIcon,
  SideBarItemName,
} from '../common/side-bar';
import { CpAccountSpacesProps, ProjectSpaceInAccount } from './types';

const ProjectSpacesListSection: React.FC<{
  projectAndSpaces: ProjectSpaceInAccount;
  currentSpace?: Space;
  search: string;
  onSpaceClick: CpAccountSpacesProps['onSpaceClick'];
}> = ({ projectAndSpaces, onSpaceClick, currentSpace, search }) => {
  const isResponsive = useIsResponsive();
  const [isExpanded, setIsExpanded] = useState(
    !!currentSpace &&
      projectAndSpaces.spaces.some(
        (space) => space.spaceId === currentSpace.spaceId,
      ),
  );

  const onChevronClick = useCallback(() => {
    setIsExpanded((p) => !p);
  }, []);

  useEffect(() => {
    setIsExpanded(
      !!currentSpace &&
        projectAndSpaces.spaces.some(
          (space) => space.spaceId === currentSpace.spaceId,
        ),
    );
  }, [currentSpace, projectAndSpaces.spaces]);

  if (!projectAndSpaces || !projectAndSpaces?.spaces) {
    return <></>;
  }
  const { spaces } = projectAndSpaces;
  const filteredSpaces = (spaces ?? []).filter(
    (space) =>
      !(
        isResponsive &&
        !!search?.trim() &&
        !space.spaceName.toLowerCase().includes(search.toLowerCase())
      ),
  );

  return (
    <ProjectWrapper isExpanded={isExpanded}>
      <ProjectNameWrapper>
        <ProjectNameAndChevronWrapper>
          <ProjectChevronWrapper onClick={onChevronClick}>
            {isExpanded ? <ChevronDown16 /> : <ChevronRight16 />}
          </ProjectChevronWrapper>
          <ProjectName>{projectAndSpaces.project.projectName}</ProjectName>
        </ProjectNameAndChevronWrapper>
      </ProjectNameWrapper>
      {!isExpanded ? (
        <></>
      ) : filteredSpaces.length > 0 ? (
        filteredSpaces.map((space) => {
          return (
            <SideBarItem
              active={currentSpace?.spaceId === space.spaceId}
              key={space.spaceId}
              onClick={() =>
                onSpaceClick({
                  projectId: projectAndSpaces.project.projectId,
                  spaceId: space.spaceId,
                })
              }
            >
              <SideBarItemIcon
                color={getRandomColor(space.spaceName, ALL_COLORS_WITHOUT_GRAY)}
              >
                <Folder16 />
              </SideBarItemIcon>
              <SideBarItemName>{space.spaceName}</SideBarItemName>
            </SideBarItem>
          );
        })
      ) : (
        <ProjectEmptyState>
          <FormattedMessage id="customer_portal.account_spaces.empty_state.title" />
        </ProjectEmptyState>
      )}
    </ProjectWrapper>
  );
};

export const CpAccountSpacesSidePane: React.FC<
  Pick<
    CpAccountSpacesProps,
    'search' | 'currentSpace' | 'onSpaceClick' | 'data'
  >
> = ({ search, currentSpace, onSpaceClick, data }) => {
  const isResponsive = useIsTablet();

  const content = useMemo(
    () => (
      <>
        <AccountsSidebar>
          {data?.spaces?.map((project) => {
            return (
              <ProjectSpacesListSection
                currentSpace={currentSpace}
                projectAndSpaces={project}
                onSpaceClick={onSpaceClick}
                search={search}
              />
            );
          })}
        </AccountsSidebar>
      </>
    ),
    [currentSpace, data?.spaces, onSpaceClick, search],
  );

  if (isResponsive) {
    return content;
  }

  return <>{!search?.trim() ? content : <></>}</>;
};
